import type {
  CmmdProviderAccountInventoryAccount,
  ProviderKind,
  ServerProvider,
} from "@cmmd-forge/contracts";
import { resolveSelectableModel } from "@cmmd-forge/shared/model";
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { Combobox, ComboboxEmpty } from "../ui/combobox";
import { TooltipProvider } from "../ui/tooltip";
import { cn } from "~/lib/utils";

import {
  PICKER_PROVIDER_ACCENT_CLASS_BY_PROVIDER,
  PICKER_PROVIDER_DISPLAY_NAME,
  PICKER_PROVIDER_ICON_BY_PROVIDER,
  type ModelPickerRowModel,
} from "./ModelListRow";
import { ModelPickerSidebar, type SidebarProviderEntry } from "./ModelPickerSidebar";
import { computeFavoriteModelKeys, useFavoriteModels } from "./modelPickerFavorites";
import {
  describeModelPickerEmptyReason,
  resolveModelPickerEmptyReason,
} from "./modelPickerEmptyState";
import { buildSidebarEntry } from "./modelPickerSidebarEntry";
import type { ModelPickerProviderAvailability } from "./modelPickerProviderAvailability";
import { describeSelectedRailProvider, useModelPickerRail } from "./useModelPickerRail";
import { ModelPickerSearchHeader, splitProviderModelKey } from "./ModelPickerSearchHeader";
import { ModelPickerInventoryAge } from "./ModelPickerInventoryAge";
import { resolveLastKnownInventoryAgeLabel } from "../newThread/lastKnownInventoryAge";
import { ProviderAccountPicker } from "./ProviderAccountPicker";
import type { CmmdOrgProviderPolicy } from "@cmmd-forge/contracts";
import { useModelPickerModels } from "./useModelPickerModels";
import { ModelPickerResults } from "./ModelPickerResults";

/**
 * Forge port of t3code's ModelPickerContent (apps/web/src/components/chat/ModelPickerContent.tsx).
 *
 * Differences from upstream:
 * - One instance per provider kind — t3code's ProviderInstanceId collapses to ProviderKind.
 *   Favorites persist in localStorage; the schema does not carry a `favorites` slot yet, so
 *   the storage key is namespaced, to migrate to server-authoritative settings later without
 *   losing data.
 * - Keybinding integration is intentionally absent. ⌘1–⌘9 jump labels render as visual hints;
 *   the global handler is a separate follow-up (would need to wire into Forge's keybindings
 *   system).
 */
export const ModelPickerContent = memo(function ModelPickerContent(props: {
  readonly activeProvider: ProviderKind;
  readonly model: string;
  readonly lockedProvider: ProviderKind | null;
  readonly modelOptionsByProvider: Record<ProviderKind, ReadonlyArray<ModelPickerRowModel>>;
  readonly providers?: ReadonlyArray<ServerProvider>;
  /**
   * Why this picker may withhold each provider it shows. Required: see
   * `ProviderModelPicker`'s prop of the same name for why an omission is not
   * a valid state here either.
   */
  readonly providerAvailability: ReadonlyArray<ModelPickerProviderAvailability>;
  readonly providerAccounts?: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  readonly selectedProviderAccountId?: string;
  readonly onProviderAccountChange?: (provider: "codex" | "claudeAgent", accountId: string) => void;
  readonly orgProviderPolicy?: CmmdOrgProviderPolicy | null;
  readonly projectSelected?: boolean;
  readonly lastKnownInventoryPublishedAt?: string | null;
  readonly onRequestClose?: () => void;
  readonly onProviderModelChange: (provider: ProviderKind, model: string) => void;
}) {
  const { modelOptionsByProvider, providers, onProviderModelChange, lockedProvider } = props;
  const orgProviderPolicy = props.orgProviderPolicy;
  const {
    rows: railRows,
    availabilityByProvider,
    railProviderKinds,
  } = useModelPickerRail({
    providers,
    policy: orgProviderPolicy,
    availability: props.providerAvailability,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProvider, setSelectedProvider] = useState<ProviderKind | "favorites">(() => {
    if (lockedProvider !== null) return lockedProvider;
    return props.activeProvider;
  });
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const highlightedKeyRef = useRef<string | null>(null);

  const { isFavorite, toggle } = useFavoriteModels();
  const favoritesSet = useMemo(
    () => computeFavoriteModelKeys(modelOptionsByProvider, isFavorite),
    [isFavorite, modelOptionsByProvider],
  );

  const focusSearchInput = useCallback(() => {
    searchInputRef.current?.focus({ preventScroll: true });
  }, []);

  useLayoutEffect(() => {
    focusSearchInput();
    const frame = window.requestAnimationFrame(focusSearchInput);
    const timeout = window.setTimeout(focusSearchInput, 0);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [focusSearchInput]);

  // If the locked provider changes (user revising a sent message), keep
  // the sidebar in sync so the visible model list matches.
  useEffect(() => {
    if (lockedProvider !== null) {
      setSelectedProvider(lockedProvider);
    }
  }, [lockedProvider]);

  const isLocked = lockedProvider !== null;
  const modelList = useModelPickerModels({
    options: modelOptionsByProvider,
    providerKinds: railProviderKinds,
    lockedProvider,
    selectedProvider,
    activeProvider: props.activeProvider,
    activeModel: props.model,
    favorites: favoritesSet,
    search: searchQuery,
  });
  const flatModels = modelList.flatModels;

  /**
   * THE canonical answer to "why is this picker empty".
   *
   * One decision point on purpose. This previously lived as two independent
   * booleans feeding a nested ternary, which is how a cause could display a
   * sentence written for a different cause (2026-08-07: no project selected
   * rendered as "Models load when this project starts"). Reasons and their
   * words now live together in modelPickerEmptyState.ts, so adding a cause
   * without adding its sentence does not compile.
   *
   * Do not reintroduce a second path here. If a new condition can empty the
   * list, it becomes a `ModelPickerEmptyReason` member — not another boolean.
   */
  const emptyReason = useMemo(
    () =>
      resolveModelPickerEmptyReason({
        hasAnyModels: flatModels.length > 0,
        projectSelected: props.projectSelected,
        providers,
      }),
    [flatModels, props.projectSelected, providers],
  );

  const allModelKeys = flatModels.map((model) => `${model.provider}:${model.slug}`);
  const filteredModelKeys = modelList.visibleModels.map(
    (model) => `${model.provider}:${model.slug}`,
  );

  const handleSelectProvider = useCallback(
    (provider: ProviderKind | "favorites") => {
      setSelectedProvider(provider);
      window.requestAnimationFrame(focusSearchInput);
    },
    [focusSearchInput],
  );

  const handleModelSelect = useCallback(
    (provider: ProviderKind, slug: string) => {
      if (availabilityByProvider.get(provider)?.state !== "usable") return;
      const options = modelOptionsByProvider[provider];
      if (!options) return;
      const resolved = resolveSelectableModel(provider, slug, options);
      if (!resolved) return;
      onProviderModelChange(provider, resolved);
    },
    [availabilityByProvider, modelOptionsByProvider, onProviderModelChange],
  );

  const sidebarEntries = useMemo<SidebarProviderEntry[]>(
    () => railRows.map((row) => buildSidebarEntry(row)),
    [railRows],
  );

  const selectedProviderSentence = describeSelectedRailProvider(
    selectedProvider,
    availabilityByProvider,
  );

  const inventoryAgeLabel = resolveLastKnownInventoryAgeLabel({
    lastKnownInventoryPublishedAt: props.lastKnownInventoryPublishedAt ?? null,
  });

  // Sidebar stays visible whenever the thread isn't already locked to a
  // provider. t3code's port hid the sidebar when the user typed in the search
  // field, but that's confusing — clicking a sidebar entry should still
  // narrow the model list while letting the user switch to another provider
  // by clicking a different rail icon. Only hide the sidebar once a turn
  // has shipped and lockedProvider is set; from that point the thread is
  // bound to one provider for the rest of its life cycle.
  const showSidebar = !isLocked;
  const LockedProviderIcon =
    isLocked && lockedProvider ? PICKER_PROVIDER_ICON_BY_PROVIDER[lockedProvider] : null;
  const lockedHeaderLabel =
    isLocked && lockedProvider ? PICKER_PROVIDER_DISPLAY_NAME[lockedProvider] : null;

  return (
    <TooltipProvider delay={0}>
      <div
        className={cn(
          "relative flex h-dvh max-h-96 w-screen max-w-100 overflow-hidden rounded-lg border bg-popover not-dark:bg-clip-padding text-popover-foreground shadow-lg/5 before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] dark:before:shadow-[0_-1px_--theme(--color-white/6%)]",
          isLocked ? "flex-col" : "flex-row",
        )}
      >
        {isLocked && LockedProviderIcon && lockedHeaderLabel ? (
          <div className="flex items-center gap-2 px-4 py-3 border-b">
            <LockedProviderIcon
              className={cn(
                "size-5 shrink-0",
                lockedProvider ? PICKER_PROVIDER_ACCENT_CLASS_BY_PROVIDER[lockedProvider] : null,
              )}
            />
            <span className="font-medium text-sm">{lockedHeaderLabel}</span>
          </div>
        ) : null}

        {showSidebar ? (
          <ModelPickerSidebar
            selectedProvider={selectedProvider}
            onSelectProvider={handleSelectProvider}
            providers={sidebarEntries}
            showFavorites
          />
        ) : null}

        <Combobox
          inline
          items={allModelKeys}
          filteredItems={filteredModelKeys}
          filter={null}
          autoHighlight
          open
          value={`${props.activeProvider}:${props.model}`}
          onItemHighlighted={(modelKey) => {
            highlightedKeyRef.current = typeof modelKey === "string" ? modelKey : null;
          }}
          onValueChange={(modelKey) => {
            if (typeof modelKey !== "string") return;
            const { provider, slug } = splitProviderModelKey(modelKey);
            handleModelSelect(provider, slug);
          }}
        >
          <div
            className={cn(
              "flex min-h-0 flex-1 flex-col overflow-hidden",
              isLocked ? "min-w-0" : showSidebar && "border-l",
            )}
          >
            {selectedProvider !== "favorites" &&
            props.providerAccounts &&
            props.onProviderAccountChange ? (
              <ProviderAccountPicker
                accounts={props.providerAccounts}
                provider={selectedProvider}
                // The selection belongs to the active provider only. Another
                // provider's tab shows its own default until the user picks an
                // account there, otherwise a Codex id is looked up among Claude
                // accounts and the strip reports it unavailable (#3315).
                selectedAccountId={
                  selectedProvider === props.activeProvider
                    ? props.selectedProviderAccountId
                    : undefined
                }
                onChange={props.onProviderAccountChange}
                inventoryAgeLabel={inventoryAgeLabel}
              />
            ) : (
              <ModelPickerInventoryAge
                lastKnownInventoryPublishedAt={props.lastKnownInventoryPublishedAt}
              />
            )}
            <ModelPickerSearchHeader
              selectedProviderSentence={selectedProviderSentence}
              searchInputRef={searchInputRef}
              highlightedKeyRef={highlightedKeyRef}
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              onModelSelect={handleModelSelect}
              onRequestClose={props.onRequestClose}
            />

            <ModelPickerResults
              models={modelList.visibleModels}
              favorites={favoritesSet}
              availability={availabilityByProvider}
              isLocked={isLocked}
              showLegacySection={modelList.showLegacySection}
              legacyStartIndex={modelList.legacyStartIndex}
              legacyCount={modelList.legacyCount}
              legacyExpanded={modelList.legacyExpanded}
              onToggleLegacy={modelList.toggleLegacy}
              onToggleFavorite={toggle}
            />
            <ComboboxEmpty className="not-empty:py-6 empty:h-0 text-xs font-normal leading-snug">
              {modelList.showLegacySection ? null : describeModelPickerEmptyReason(emptyReason)}
            </ComboboxEmpty>
          </div>
        </Combobox>
      </div>
    </TooltipProvider>
  );
});
